import {
  FC,
  HTMLAttributes,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Repeat } from 'react-ionicons';
import { usePopper } from 'react-popper';
import { formatMobilityPrice } from '../utils';
import { SelectedIndices } from '../BaseComponents/ProductSummary';
import { ProductType, VariantsType } from '../accessExpress.types';

interface PricingProps {
  selectedIndices: SelectedIndices;
  product: ProductType;
}

const findLowestPricesForMobilityProduct = (variants: VariantsType[]) => {
  let lowestNrc = null as number | null;
  let lowestMrc = null as number | null;

  for (const variant of variants) {
    for (const child of variant.children) {
      if (child.productGroupDesc === 'PURCHASED')
        lowestNrc =
          lowestNrc !== null
            ? Math.min(lowestNrc, child.originalPrice)
            : child.originalPrice;
      else if (child.productGroupDesc === 'FINANCED')
        lowestMrc =
          lowestMrc !== null
            ? Math.min(lowestMrc, child.originalPrice)
            : child.originalPrice;
    }
  }
  return { nrc: lowestNrc, mrc: lowestMrc };
};

const Pricing: FC<PricingProps> = ({ selectedIndices, product }) => {
  const { selectedVariant, selectedPricingOption } = useMemo(() => {
    if (!product?.varients)
      return { selectedVariant: null, selectedPricingOption: null };
    const selectedVariant =
      selectedIndices.variantIndex !== null
        ? product.varients[selectedIndices.variantIndex]
        : null;
    const selectedPricingOption =
      selectedVariant && selectedIndices.pricingOptionIndex !== null
        ? selectedVariant.children[selectedIndices.pricingOptionIndex]
        : null;
    return { selectedVariant, selectedPricingOption };
  }, [selectedIndices, product]);

  const dynamicPricing = useMemo(() => {
    if (!product?.varients) return null;
    if (selectedVariant === null)
      return findLowestPricesForMobilityProduct(product.varients);

    let lowestMrc = null;
    let lowestNrc = null;
    for (const option of selectedVariant.children) {
      if (
        option.productGroupDesc === 'FINANCED' &&
        (lowestMrc === null || option.originalPrice < lowestMrc)
      )
        lowestMrc = option.originalPrice;
      else if (
        option.productGroupDesc === 'PURCHASED' &&
        (lowestNrc === null || option.originalPrice < lowestNrc)
      )
        lowestNrc = option.originalPrice;
    }

    if (selectedPricingOption !== null) {
      if (selectedPricingOption.productGroupDesc === 'FINANCED')
        return { mrc: selectedPricingOption.originalPrice, nrc: lowestNrc };
      else if (selectedPricingOption.productGroupDesc === 'PURCHASED')
        return { mrc: lowestMrc, nrc: selectedPricingOption.originalPrice };
    }
    return { mrc: lowestMrc, nrc: lowestNrc };
  }, [product?.varients, selectedVariant, selectedPricingOption]);

  if (dynamicPricing === null) return null;

  return (
    <div className="flex gap-8">
      {dynamicPricing?.mrc !== null && (
        <Costs
          price={dynamicPricing.mrc}
          pricingConfirmed={
            selectedIndices.pricingOptionIndex !== null &&
            selectedPricingOption?.productGroupDesc === 'FINANCED'
          }
          type="MRC"
          tooltipMessage="Monthly recurring charge"
        />
      )}
      {dynamicPricing?.nrc !== null && (
        <Costs
          price={dynamicPricing.nrc}
          pricingConfirmed={selectedIndices.variantIndex !== null}
          type="NRC"
          tooltipMessage="Non-recurring charge"
        />
      )}
    </div>
  );
};

export default Pricing;

interface CostsProps {
  type: 'NRC' | 'MRC';
  pricingConfirmed: boolean;
  price?: number;
  tooltipMessage: string;
}

const Costs: FC<CostsProps> = ({
  price,
  pricingConfirmed,
  type,
  tooltipMessage,
}) => {
  return (
    <CostsTooltip message={tooltipMessage}>
      <div className="flex items-end gap-1">
        <div className="flex flex-col items-end">
          <p className="text-sm font-bold text-content-flip-subdued">{type}</p>
          <p>
            {!pricingConfirmed && (
              <span className="mr-2 text-xs text-content-base-subdued">
                Starting at
              </span>
            )}
            <span className="text-xl font-bold text-content-base-default">
              ${formatMobilityPrice(price || 0)}
            </span>
          </p>
        </div>
        {type === 'MRC' && (
          <span className="mb-1.5 text-accent-blue">
            <Repeat width="16px" height="16px" color="currentColor" />
          </span>
        )}
      </div>
    </CostsTooltip>
  );
};

interface CostsTooltipProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  message: string;
}

const CostsTooltip: FC<CostsTooltipProps> = ({
  children,
  message,
  ...props
}) => {
  const [visible, setVisible] = useState(false);
  const referenceRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const { styles, attributes, forceUpdate } = usePopper(
    referenceRef.current,
    popperRef.current,
    {
      placement: 'bottom',
      strategy: 'fixed',
      modifiers: [{ name: 'offset', options: { offset: [0, 10] } }],
    },
  );
  return (
    <div
      {...props}
      ref={referenceRef}
      onMouseEnter={() => {
        setVisible(true);
        forceUpdate?.();
      }}
      onMouseLeave={() => {
        setVisible(false);
      }}
    >
      <div
        ref={popperRef}
        style={styles.popper}
        {...attributes.popper}
        className={`pointer-events-none z-[100] w-max transition-opacity duration-150 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="relative rounded bg-background-base-surface-0 p-5 text-sm shadow-elevation3">
          {message}
          <div className="absolute -top-1 right-1/3 scale-x-[0.84] transform">
            <div className="h-[17px] w-[17px] rotate-45 bg-background-base-surface-0" />
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};
